import { getCurrentDate, getDisplayDate } from '../../utils/DateUtils';
import { mapAllowedPlants, pickDefaultPlantId } from './designMixDefaults';
import moment from 'moment';

export { mapAllowedPlants, pickDefaultPlantId };

export const PENDING_AGE_OPTIONS = [7, 28];
export const PENDING_DEFAULT_LOOKBACK_DAYS = 90;
export const CUBE_ID_COLUMN_MIN_WIDTH = '220px';
export const CUBE_ID_DISPLAY_STYLE = {
  width: '100%',
  minWidth: CUBE_ID_COLUMN_MIN_WIDTH,
  backgroundColor: 'white',
  cursor: 'not-allowed',
  textAlign: 'center',
  boxSizing: 'border-box',
  overflow: 'visible',
  fontSize: '0.92rem',
  paddingLeft: '6px',
  paddingRight: '6px',
};
export const CUBE_ID_CELL_STYLE = {
  minWidth: CUBE_ID_COLUMN_MIN_WIDTH,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export function getDefaultDcPeriod() {
  const today = moment();
  return {
    fromDate: today.clone().subtract(PENDING_DEFAULT_LOOKBACK_DAYS, 'days'),
    uptoDate: today.clone(),
  };
}

/** Caps the selected DC period to dates eligible for the given pending age (API query). */
export function resolvePendingQueryRange(ageDays, fromDate, uptoDate) {
  const today = moment();
  const latestDue = today.clone().subtract(ageDays, 'days');

  let queryUpto = uptoDate.clone();
  if (!queryUpto.isValid()) {
    queryUpto = latestDue.clone();
  }
  if (queryUpto.isAfter(latestDue, 'day')) {
    queryUpto = latestDue.clone();
  }

  let queryFrom = fromDate.clone();
  if (!queryFrom.isValid()) {
    queryFrom = getDefaultDcPeriod().fromDate;
  }
  if (queryFrom.isAfter(queryUpto, 'day')) {
    queryFrom = queryUpto.clone();
  }

  return { fromDate: queryFrom, uptoDate: queryUpto };
}

export function getPendingQueryRange(ageDays) {
  const { fromDate, uptoDate } = getDefaultDcPeriod();
  return resolvePendingQueryRange(ageDays, fromDate, uptoDate);
}

export function formatPendingRangeLabel(fromDate, uptoDate) {
  return `${fromDate.format('DD-MM-YYYY')} → ${uptoDate.format('DD-MM-YYYY')}`;
}

export function buildPendingListTitle(ageDays, fromDate, uptoDate) {
  return `${ageDays} Days Pending — Casting ${formatPendingRangeLabel(fromDate, uptoDate)}`;
}

export function getInitialCubeTestReportFormState() {
  return {
    csr_date: getCurrentDate(),
    plant_id: '',
    casting_dt: '',
    cured_place: '',
    cement_type: '',
    cement_content: '',
    technician_name: '',
    no_of_moulds: 0,
    user_remarks: '',
    csr_no: '',
    prefix: '',
    csr_num_prefix: '',
    pending_age_days: '',
  };
}

export function buildReportNumberDisplay(prefix, reportNo) {
  if (!prefix && !reportNo) {
    return '';
  }
  return `${prefix || ''}${reportNo || ''}`;
}

export function mapPendingListRecords(records) {
  return (records || []).map((item, index) => ({
    ...item,
    id: item.id || index + 1,
  }));
}

export function castingDateInputFromApi(displayDate) {
  if (!displayDate) {
    return '';
  }
  return getDisplayDate(displayDate);
}

export function reportDateFromCastingAndAge(castingDateInput, ageDays) {
  if (!castingDateInput || !ageDays) {
    return '';
  }
  const parsed = moment(castingDateInput, ['YYYY-MM-DD', 'DD-MM-YYYY'], true);
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.add(Number(ageDays), 'days').format('YYYY-MM-DD');
}

export function buildCustomerInitials(customerName) {
  if (!customerName) {
    return '';
  }

  return String(customerName)
    .trim()
    .split(/\s+/)
    .flatMap((word) => {
      const cleaned = word.replace(/[^a-zA-Z0-9&]/g, '');
      if (!cleaned) {
        return [];
      }
      if (cleaned.includes('&')) {
        return cleaned.split('&').filter(Boolean).map((part) => part.charAt(0));
      }
      return [cleaned.charAt(0)];
    })
    .join('')
    .toUpperCase();
}

export function buildGradePart(gradeName) {
  if (!gradeName) {
    return '';
  }
  return String(gradeName).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

export function formatDcDateForCubeId(dateInput) {
  if (!dateInput) {
    return '';
  }
  const parsed = moment(dateInput, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD-MM-YY'], true);
  if (!parsed.isValid()) {
    return '';
  }
  return parsed.format('DDMMYY');
}

export function buildCubeId({ customerName, gradeName, ageDays, dcDateInput }) {
  const initials = buildCustomerInitials(customerName);
  const gradePart = buildGradePart(gradeName);
  const agePart = ageDays != null && ageDays !== ''
    ? String(ageDays).padStart(2, '0')
    : '';
  const datePart = formatDcDateForCubeId(dcDateInput);

  return `${initials}${gradePart}${agePart}${datePart}`;
}

export function buildCubeIdFromSelection(selection, ageDays, dcDateInput) {
  if (!selection?.customer_name) {
    return '';
  }
  return buildCubeId({
    customerName: selection.customer_name,
    gradeName: selection.product_name,
    ageDays,
    dcDateInput: dcDateInput || selection.delivery_challan_date || selection.casting_dt,
  });
}

export function resolveNextAgeDays(existingRows, pendingAgeDays) {
  const ages = new Set((existingRows || []).map((row) => Number(row.age)));
  if (!ages.has(7)) {
    return 7;
  }
  if (!ages.has(28)) {
    return 28;
  }
  return pendingAgeDays || 7;
}

export function syncCubeIdsForAgeRows(rowsData, selection, dcDateInput) {
  if (!selection?.customer_name || !rowsData?.length) {
    return rowsData;
  }
  const dcDate = dcDateInput || selection.delivery_challan_date || selection.casting_dt;
  return rowsData.map((row) => ({
    ...row,
    cube_id: buildCubeIdFromSelection(selection, row.age, dcDate),
  }));
}

/** @deprecated Use buildCubeIdFromSelection */
export function buildCubeIdPrefixFromSelection(selection, ageDays) {
  return buildCubeIdFromSelection(selection, ageDays);
}

export function buildCubeSampleId(baseId, index, total) {
  const clean = String(baseId || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (!clean) {
    return '';
  }
  if (total <= 1) {
    return clean;
  }
  return `${clean}${index}`;
}

export function applyAgeRowFieldChange(rowsData, index, name, value, selection, dcDateInput) {
  const rowsInput = [...rowsData];
  rowsInput[index][name] = value;

  if (name === 'age' && selection?.customer_name) {
    rowsInput[index].cube_id = buildCubeIdFromSelection(
      selection,
      value,
      dcDateInput || selection.delivery_challan_date || selection.casting_dt,
    );
  }

  return rowsInput;
}

export function buildPendingAgeRow(ageDays, cubeId = '', curedPlace = 'plant') {
  return {
    age: ageDays,
    no_of_moulds: 0,
    cube_id: cubeId,
    cured_place: curedPlace,
  };
}

export function buildCsrRowsFromAgeSetup(rowsData, castingDateInput, mode = 'create') {
  if (!castingDateInput || !rowsData?.length) {
    return [];
  }

  const newCsrRows = [];
  let serialNumber = 1;

  rowsData.forEach((row) => {
    const { age, no_of_moulds, cube_id: cubeId } = row;
    const moulds = parseInt(no_of_moulds, 10) || 0;
    if (!moulds) {
      return;
    }

    const testingDate = new Date(castingDateInput);
    testingDate.setDate(testingDate.getDate() + Number(age));
    const dateStr = testingDate.toISOString().split('T')[0];

    for (let i = 1; i <= moulds; i += 1) {
      const sampleId = buildCubeSampleId(cubeId, i, moulds);
      if (mode === 'edit') {
        newCsrRows.push({
          id: '',
          sl_no: serialNumber,
          age: parseInt(age, 10),
          sample_id: sampleId,
          age_date: dateStr,
          no_of_rows: moulds,
          act_weight: 0,
          act_load: 0,
          cus_weight: 0,
          cus_load: 0,
          ref_cube_strength: 0,
          act_strength: 0,
          act_difference: 0,
          cus_strength: 0,
          cus_difference: 0,
          user_remarks: '',
        });
      } else {
        newCsrRows.push({
          sl_no: serialNumber,
          age: parseInt(age, 10),
          cube_id: sampleId,
          testing_date: dateStr,
          no_of_rows: moulds,
          act_weight: 0,
          act_load: 0,
          cus_weight: 0,
          cus_load: 0,
          act_strength: 0,
          ref_cube_strength: 0,
          act_difference: 0,
          cus_strength: 0,
          cus_difference: 0,
        });
      }
      serialNumber += 1;
    }
  });

  return newCsrRows;
}

export function resolveCuredPlaceFromAgeRows(rowsData, fallback = '') {
  const rowWithPlace = (rowsData || []).find((row) => row.cured_place);
  return rowWithPlace?.cured_place || fallback;
}

export function getPendingColumns(includeStatus = true) {
  const columns = [
    { fieldName: 'id', headerName: 'ROW_ACTIONS', size: 40 },
    { fieldName: 'delivery_challan_date', headerName: 'Casting Date', size: 50 },
    { fieldName: 'customer_name', headerName: 'Customer', size: 180 },
    { fieldName: 'project_name', headerName: 'Site', size: 180 },
    { fieldName: 'product_name', headerName: 'Grade', size: 50 },
    { fieldName: 'total_dc_qty', headerName: 'Dc Qty', size: 50 },
  ];
  if (includeStatus) {
    columns.splice(1, 0, { fieldName: 'pending_status', headerName: 'Status', size: 60 });
  }
  return columns;
}

export function decoratePendingRecords(records) {
  return mapPendingListRecords(records).map((item) => ({
    ...item,
    pending_status: item.pending_type === 'update'
      ? `Update${item.report_no ? ` (${item.report_no})` : ''}`
      : 'New',
  }));
}
